import React, { type CSSProperties, type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from 'aphrodite';
import { t } from 'i18next';

import { TrackingProps } from 'Services/trackingService';

import UIconButton from 'Components/unit/UIconButton/UIconButton';
import UIconToggle from 'Components/unit/UIconToggle/UIconToggle';
import UButton from 'Components/unit/UButton/UButton';
import UToggle from 'Components/unit/UToggle/UToggle';
import UTopActionBar from 'Components/unit/UTopActionBar/UTopActionBar';
import UDropdownBox from 'Components/unit/UDropdownBox/UDropdownBox';
import UDropdownItem from 'Components/unit/UDropdownItem/UDropdownItem';
import ULanguageMenu from 'Components/unit/ULanguageMenu/ULanguageMenu';
import STabList from 'Components/structural/STabList/STabList';
import withTooltip from 'Components/hoc/withTooltip/withTooltip';
import type { StatusType, HelperType } from 'Components/unit/UTopActionBar/UTopActionBar';
import type { OptionType as LanguageOptionType } from 'Components/unit/ULanguageMenu/ULanguageMenu';

import styles, { conditionalStyles, extendedStyles } from './SActionBar.style';

export type OptionType = {
  title: string;
  callback: () => void;
} & TrackingProps;

export type SActionBarProps = {
  isActive?: boolean;
  activeToggleLabel?: string;
  inactiveToggleLabel?: string;
  lastSaved?: string;
  canSave?: boolean;
  onSave: () => void;
  onBack: () => void;
  canActivate?: boolean;
  tooltipText?: string;
  id: number;
  title: string;
  titlePlaceholder?: string;
  status: StatusType;
  statusWording: string;
  isTranslated?: boolean;
  helper?: HelperType;
  tabs: string[];
  selectedTab: number;
  onChangeTab: (tab: number) => void;
  options: Readonly<OptionType[]> | OptionType[];
  dotMenu?: boolean;
  backArrow?: boolean;
  optionsLanguages: Readonly<LanguageOptionType[]> | LanguageOptionType[];
  onToggleChange: (toggled: boolean) => void;
  isToggleLoading?: boolean;
  isSaveLoading?: boolean;
  onLock: (lock: boolean) => void;
  locked?: boolean;
  canLock?: boolean;
  style?: CSSProperties;
  useConditionalStyles?: boolean;
};

const languageOptionTypeHasProps = (
  option: LanguageOptionType,
): option is Extract<LanguageOptionType, { props: unknown }> => {
  return 'props' in option;
};

export const SActionBar: FC<SActionBarProps> = ({
  isActive = false,
  activeToggleLabel = 'Active',
  inactiveToggleLabel = 'Inactive',
  isToggleLoading = false,
  isSaveLoading = false,
  lastSaved = '',
  canSave = false,
  isTranslated = false,
  helper,
  locked = false,
  canLock = true,
  style,
  canActivate = true,
  tooltipText = '',
  titlePlaceholder = '',
  dotMenu = false,
  backArrow = false,
  id,
  title,
  status,
  statusWording,
  tabs,
  selectedTab,
  options,
  optionsLanguages,
  onSave,
  onBack,
  onChangeTab,
  onToggleChange,
  onLock,
  useConditionalStyles = false,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const WrappedCompToggle = withTooltip(UToggle);
  const [displayOption, setDisplayOption] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && event.target instanceof Node && !wrapperRef.current.contains(event.target)) {
        setDisplayOption(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleOptionItemClick = useCallback(
    (callback: () => void) => {
      setDisplayOption(false);
      callback();
    },
    [setDisplayOption],
  );

  const getCountryCode = useCallback(
    (optionsLanguages: Readonly<LanguageOptionType[]> | LanguageOptionType[]) => {
      const selectedLanguage = optionsLanguages.find(
        (optionLanguage) =>
          optionLanguage.type === 'language' &&
          languageOptionTypeHasProps(optionLanguage) &&
          optionLanguage.props.selected,
      );

      return selectedLanguage && languageOptionTypeHasProps(selectedLanguage) ? selectedLanguage.props.countryCode : '';
    },
    [optionsLanguages],
  );

  const appliedStyles = useMemo(
    () => (useConditionalStyles ? conditionalStyles : extendedStyles),
    [useConditionalStyles],
  );

  return (
    <div style={{ ...styles.wrapper, ...style }} className={css(appliedStyles.wrapper)}>
      <div style={styles.wrapperInfos} className={css(appliedStyles.border)}>
        {backArrow ? (
          <UIconButton
            icon="arrow-left"
            onClick={onBack}
            ghost
            style={{ ...styles.backButton, ...styles.backArrowButton }}
            dataTestContext="sactionbar-back-arrow"
          />
        ) : (
          <UButton
            text={t('structural_component:s_action_bar.back')}
            onClick={onBack}
            style={styles.backButton}
            dataTestContext="sactionbar-back"
          />
        )}

        <UTopActionBar
          id={id}
          title={title}
          placeholder={titlePlaceholder}
          status={status}
          statusWording={statusWording}
          isTranslated={isTranslated}
          helper={helper}
          style={styles.topActionBar}
        />
      </div>

      <div style={styles.wrapperTabs} className={css(appliedStyles.wrapperTabs)}>
        <STabList
          tabs={tabs}
          selectedItem={selectedTab}
          onChangeTab={onChangeTab}
          align="center"
          data-testid="tab-item"
        />
      </div>

      <div style={styles.wrapperActions} className={css(appliedStyles.border)}>
        <div style={styles.lastEdit}>{lastSaved}</div>

        <UButton
          text={t('structural_component:s_action_bar.save')}
          disabled={!canSave}
          type={!canSave ? 'standard' : 'accentuated'}
          style={{ ...styles.saveButton, ...(dotMenu ? styles.saveButtonWithoutOptionsMenu : {}) }}
          onClick={onSave}
          loading={isSaveLoading}
          dataTestContext="sactionbar-save"
        />

        {!dotMenu && (
          <div ref={wrapperRef} style={styles.optionButton}>
            <UIconButton
              icon="options"
              onClick={() => setDisplayOption(!displayOption)}
              ghost
              dataTestContext="sactionbar-options"
            />
            {displayOption && (
              <UDropdownBox
                items={options}
                renderItem={(item: OptionType) => (
                  <UDropdownItem
                    key={item.title}
                    text={item.title}
                    onClick={() => handleOptionItemClick(item.callback)}
                    trackingAction={item.trackingAction}
                    trackingContext={item.trackingContext}
                  />
                )}
                style={styles.optionDropDown}
              />
            )}
          </div>
        )}

        <ULanguageMenu
          countryCode={getCountryCode(optionsLanguages)}
          options={optionsLanguages}
          style={{ ...styles.languageButton, ...(dotMenu ? styles.languageButtonWithoutOptionsMenu : {}) }}
        />

        <div style={styles.spacer} />

        <WrappedCompToggle
          tooltipPosition="left"
          tooltipContent={tooltipText}
          tooltipEnabled={!canActivate}
          tooltipMaxWidth={400}
          type="large"
          onChange={onToggleChange}
          style={styles.toggle}
          toggled={isActive}
          toggledLabel={activeToggleLabel}
          unToggledLabel={inactiveToggleLabel}
          isLocked={locked}
          isLoading={isToggleLoading}
          isClickable={canActivate}
          data-testid="active-toggle"
        />

        {canLock && (
          <UIconToggle
            iconOn="lock"
            textOn={t('structural_component:s_action_bar.lock')}
            iconOff="unlock"
            textOff={t('structural_component:s_action_bar.unlock')}
            toggled={locked}
            onToggle={() => onLock(!locked)}
            style={styles.lock}
            data-testid="lock-button"
          />
        )}

        {dotMenu && (
          <div ref={wrapperRef} style={{ ...styles.optionButton, ...styles.dotMenuButtonIcon }}>
            <UIconButton
              icon="dot-menu"
              onClick={() => setDisplayOption(!displayOption)}
              ghost
              data-testid="dot-menu-button"
            />
            {displayOption && (
              <UDropdownBox
                items={options}
                renderItem={(item: OptionType) => (
                  <UDropdownItem
                    key={item.title}
                    text={item.title}
                    onClick={() => handleOptionItemClick(item.callback)}
                    trackingAction={item.trackingAction}
                    trackingContext={item.trackingContext}
                  />
                )}
                style={styles.optionDropDown}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SActionBar;
